import { supabase } from '@/integrations/supabase/client';
import { isOfflineMode } from './offlineMode';
import { productionLogger } from './production-logger';

interface GuardOptions {
  fallbackData?: any;
  allowOffline?: boolean;
  errorMessage?: string;
}

interface GuardResult<T = any> {
  data: T | null;
  error: Error | null;
  isOffline: boolean;
}

// Quick connectivity check with timeout
const checkConnectivity = async (timeoutMs: number = 2000): Promise<boolean> => {
  if (isOfflineMode()) return false;
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    const response = await fetch(
      'https://ltwjahlnrxdkhnghsfka.supabase.co/health',
      { 
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      }
    );
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
};

// Guard for Supabase database operations
export const guardedSupabaseQuery = async <T = any>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  options: GuardOptions = {}
): Promise<GuardResult<T>> => {
  const { fallbackData = null, allowOffline = true, errorMessage = 'Service temporarily unavailable' } = options;

  // Check offline mode first
  if (isOfflineMode()) {
    return {
      data: allowOffline ? fallbackData : null,
      error: new Error(errorMessage),
      isOffline: true
    };
  }

  try {
    const result = await queryFn();
    return {
      data: result.data,
      error: result.error ? new Error(result.error.message) : null,
      isOffline: false
    };
  } catch (error) {
    productionLogger.error('Supabase query failed:', error);
    return {
      data: allowOffline ? fallbackData : null,
      error: error instanceof Error ? error : new Error(errorMessage),
      isOffline: true
    };
  }
};

// Guard for Supabase auth operations
export const guardedSupabaseAuth = async <T = any>(
  authFn: () => Promise<T>,
  options: GuardOptions = {}
): Promise<GuardResult<T>> => {
  const { fallbackData = null, errorMessage = 'Authentication service unavailable' } = options;

  if (isOfflineMode()) {
    return {
      data: fallbackData,
      error: new Error(errorMessage),
      isOffline: true
    };
  }

  try {
    const result = await authFn();
    return {
      data: result,
      error: null,
      isOffline: false
    };
  } catch (error) {
    productionLogger.error('Supabase auth failed:', error);
    return {
      data: fallbackData,
      error: error instanceof Error ? error : new Error(errorMessage),
      isOffline: true
    };
  }
};

// Simplified direct access to supabase client with guard awareness
export const guardedSupabase = supabase;